import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Server,
  Globe,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import IngressManagementPanel from "../../components/tenants/IngressManagementPanel";
import DNSRecordsPanel from "../../components/tenants/DNSRecordsPanel";
import tenantService, { CloudFlareDnsRecord, TenantDnsRawRecord, TenantIngressHistory } from "../../services/tenant.service";

// ---------- Types ----------

interface IngressData {
  commit_id: string;
  status: string;
  updated_at: string;
  history: TenantIngressHistory[];
  yaml?: string;
}

// ---------- Status helpers ----------

const INGRESS_STATUS: Record<string, { icon: React.ReactNode; label: string; cls: string }> = {
  SUCCESS: { icon: <CheckCircle2 className="w-4 h-4" />, label: "Deployed", cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  RUNNING: { icon: <Loader2 className="w-4 h-4 animate-spin" />, label: "Running", cls: "text-blue-600 bg-blue-50 border-blue-200" },
  FAILED: { icon: <XCircle className="w-4 h-4" />, label: "Failed", cls: "text-red-600 bg-red-50 border-red-200" },
  PENDING: { icon: <Clock className="w-4 h-4" />, label: "Pending", cls: "text-amber-600 bg-amber-50 border-amber-200" },
};

const normalizeTenantDnsRecord = (r: TenantDnsRawRecord): CloudFlareDnsRecord => ({
  id: r.record_id,
  name: r.hostname,
  type: r.type,
  content: r.target,
  proxied: r.proxied,
  created_on: r.created_at,
});

// ---------- Component ----------

type Tab = "ingress" | "dns";

const TenantInfrastructure: React.FC = () => {
  const { tenant_key } = useParams<{ tenant_key: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("ingress");

  const [ingress, setIngress] = useState<IngressData | null>(null);
  const [dnsRecords, setDnsRecords] = useState<CloudFlareDnsRecord[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [refreshingIngress, setRefreshingIngress] = useState(false);
  const [refreshingDns, setRefreshingDns] = useState(false);

  const fetchTenant = useCallback(async () => {
    if (!tenant_key) return;
    try {
      const [tenantRes, dnsRes] = await Promise.all([
        tenantService.getTenantByKey(tenant_key),
        tenantService.getTenantDNSRecords(tenant_key),
      ]);
      setIngress(tenantRes.data.integrations?.gitlab_ingress ?? null);
      setDnsRecords((dnsRes.data.records ?? []).map(normalizeTenantDnsRecord));
    } catch {
      toast.error("Failed to load infrastructure data");
    }
  }, [tenant_key]);

  useEffect(() => {
    setPageLoading(true);
    fetchTenant().finally(() => setPageLoading(false));
  }, [fetchTenant]);

  const refreshIngress = async () => {
    setRefreshingIngress(true);
    await fetchTenant();
    setRefreshingIngress(false);
  };

  const refreshDNS = async () => {
    setRefreshingDns(true);
    await fetchTenant();
    setRefreshingDns(false);
  };

  // Summary card data
  const ingressStatusKey = (ingress?.status ?? "PENDING").toUpperCase();
  const ingressStatusCfg = INGRESS_STATUS[ingressStatusKey] ?? INGRESS_STATUS["PENDING"];
  const proxiedCount = dnsRecords.filter((r) => r.proxied).length;

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Back navigation */}
      <button
        onClick={() => navigate("/enterprise/tenants")}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Tenants
      </button>

      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">
          Infrastructure —{" "}
          <span className="font-mono text-blue-600">{tenant_key}</span>
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage Kubernetes Ingress and CloudFlare DNS for this tenant
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Ingress status card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Server className="w-4 h-4 text-slate-600" />
            </div>
            <span className="text-sm font-semibold text-slate-700">Kubernetes Ingress</span>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold ${ingressStatusCfg.cls}`}>
            {ingressStatusCfg.icon}
            {ingressStatusCfg.label}
          </span>
          {ingress?.commit_id && (
            <p className="mt-2 text-xs text-slate-400 font-mono truncate">
              {ingress.commit_id.slice(0, 12)}
            </p>
          )}
        </div>

        {/* DNS records card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Globe className="w-4 h-4 text-slate-600" />
            </div>
            <span className="text-sm font-semibold text-slate-700">DNS Records</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{dnsRecords.length}</p>
          <p className="text-xs text-slate-400 mt-0.5">{proxiedCount} proxied through CloudFlare</p>
        </div>

        {/* Health indicator card */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-slate-100 rounded-lg">
              <AlertCircle className="w-4 h-4 text-slate-600" />
            </div>
            <span className="text-sm font-semibold text-slate-700">Overall Health</span>
          </div>
          {ingressStatusKey === "SUCCESS" && dnsRecords.length > 0 ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold text-emerald-600 bg-emerald-50 border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Healthy
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold text-amber-600 bg-amber-50 border-amber-200">
              <AlertCircle className="w-3.5 h-3.5" />
              Needs attention
            </span>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab("ingress")}
            className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              activeTab === "ingress"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Server className="w-4 h-4" />
            Kubernetes Ingress
          </button>
          <button
            onClick={() => setActiveTab("dns")}
            className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              activeTab === "dns"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Globe className="w-4 h-4" />
            CloudFlare DNS
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
              {dnsRecords.length}
            </span>
          </button>
        </div>

        <div className="p-6">
          {activeTab === "ingress" && (
            <IngressManagementPanel
              tenantKey={tenant_key ?? ""}
              ingress={ingress}
              loading={refreshingIngress}
              onRefresh={refreshIngress}
            />
          )}
          {activeTab === "dns" && (
            <DNSRecordsPanel
              tenantKey={tenant_key ?? ""}
              records={dnsRecords}
              loading={refreshingDns}
              onRefresh={refreshDNS}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantInfrastructure;
